import { ListFilter, Check } from "lucide-react";
import { Filter } from "@/shared/lib/filter/filter";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import t from "@/lang/lang";
import { cn } from "@/shared/lib/utils";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { Button } from "@/shared/components/ui/button";
import { useProcessesProcessListOwningUsersQuery } from "@/services/backend/processes/api/process-api";
import { Spinner } from "@/shared/components/ui/spinner";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useShowUserQuery } from "@/services/ms-graph/ms-graph-api";
import { UUID } from "@bitory-gmbh/nidavellir";

export function ProcessOwningUserFilter({
  filter,
  onFilterChange,
  hidden = false,
}: {
  filter: Filter<string> | undefined;
  onFilterChange: (request: Filter<string>) => void;
  hidden?: boolean;
}) {
  // This allows us to accept undefined filters,
  // which is common in list requests. This makes
  // it nicer to call the component.
  const filterVal = filter ?? { active: false, values: [] };
  // simplifies some code below
  const currentValues = filterVal.values ?? [];

  const { data, isLoading, error } = useProcessesProcessListOwningUsersQuery(
    {},
  );

  if (hidden) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <ListFilter />
          {t("Besitzer")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <Command>
          <CommandInput placeholder={t("Name")} />
          {isLoading && (
            <span className="flex w-full items-center justify-center gap-2 py-3 text-sm">
              <Spinner />
              {t("Lade Daten ...")}
            </span>
          )}
          {error && (
            <RTKQueryErrorAlert error={error} className="border-none" />
          )}
          <CommandList>
            <CommandGroup>
              {data &&
                data.items.map((owningUserId) => (
                  <UserCommandItem
                    key={owningUserId}
                    owningUserId={owningUserId}
                    currentValues={currentValues}
                    onFilterChange={onFilterChange}
                  />
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function UserCommandItem({
  owningUserId,
  currentValues,
  onFilterChange,
}: {
  owningUserId: string;
  currentValues: UUID[];
  onFilterChange: (f: Filter<UUID>) => void;
}) {
  const { data } = useShowUserQuery({ id: owningUserId });

  const user = data ?? {
    id: owningUserId,
    displayName: owningUserId,
  };

  return (
    <CommandItem
      key={user.id}
      value={user.displayName}
      onSelect={() => {
        const values = currentValues.includes(user.id)
          ? currentValues.filter((v) => v !== user.id)
          : [...currentValues, user.id];
        onFilterChange({ active: values.length > 0, values });
      }}
    >
      <Check
        className={cn(
          "mr-2 h-4 w-4",
          currentValues.includes(user.id) ? "opacity-100" : "opacity-0",
        )}
      />
      {user.displayName}
    </CommandItem>
  );
}
